import { Download, Inbox } from 'lucide-react';
import { useState } from 'react';

import {
  useAuditoriaAdminControllerAutoresDoFunil,
  useAuditoriaAdminControllerListarFunil,
  useAuditoriaAdminControllerResumoDoFunil,
} from '@/api/generated/admin-auditoria/admin-auditoria';
import type { RascunhoDoFunilResponse } from '@/api/generated/model';
import { rotuloDiasParaEncerrar } from '@/features/discussoes/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { ExportarCsvDialogo } from '@/shared/components/ExportarCsvDialogo';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODAS = 'todas';
const TODOS = 'todos';

/** Mesmo total que o wizard mostra em "Passo X de 8" — ver `ETAPAS` em `AberturaDiscussaoWizard.tsx`. */
const TOTAL_ETAPAS_WIZARD = 8;

/** `etapa` chega 0-based do servidor — "Etapa 4/8" fala a mesma língua do "Passo 4 de 8" do wizard. */
function rotuloEtapa(etapa: number, etapaNome: string): string {
  return `Etapa ${etapa + 1}/${TOTAL_ETAPAS_WIZARD} — ${etapaNome}`;
}

/** Dias corridos desde o último passo salvo — mesma conta que decide a expiração automática (7 dias). */
function diasParado(progressoAtualizadoEm: string): number {
  const ms = Date.now() - new Date(progressoAtualizadoEm).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/**
 * Filtros de status. "Abertas" são dois status juntos — o servidor aceita a
 * lista separada por vírgula.
 */
const FILTROS_DE_STATUS = [
  { valor: 'RASCUNHO', rotulo: 'Rascunhos ativos' },
  { valor: 'RASCUNHO_EXPIRADO', rotulo: 'Rascunhos expirados' },
  { valor: 'ABERTA,EM_ATENDIMENTO', rotulo: 'Abertas' },
  { valor: 'ENCERRADA', rotulo: 'Encerradas' },
  { valor: 'CANCELADA', rotulo: 'Canceladas' },
] as const;

/** Filtro de rascunho: aí o que importa é quem está parado há mais tempo. */
function filtraRascunho(status: string): boolean {
  return status === 'RASCUNHO' || status === 'RASCUNHO_EXPIRADO';
}

/**
 * Espelha `rotuloDaDiscussao`/`classeDaDiscussao` de `features/discussoes/rotulos.ts`,
 * mas a partir de `advisorNome` (o que esta listagem recebe), não `assessorId` —
 * shapes diferentes o bastante pra não valer forçar uma função só.
 */
function statusDaDiscussao(item: Pick<RascunhoDoFunilResponse, 'status' | 'advisorNome'>): {
  rotulo: string;
  classe: string;
} {
  switch (item.status) {
    case 'RASCUNHO_EXPIRADO':
      return { rotulo: 'Expirado', classe: 'text-muted-foreground' };
    case 'RASCUNHO':
      return item.advisorNome
        ? { rotulo: 'Aguardando pagamento', classe: 'text-amber-600 border-amber-600/40' }
        : { rotulo: 'Em preenchimento', classe: '' };
    case 'ABERTA':
      return { rotulo: 'Aberta', classe: 'text-emerald-600 border-emerald-600/40' };
    case 'EM_ATENDIMENTO':
      return { rotulo: 'Em atendimento', classe: 'text-emerald-600 border-emerald-600/40' };
    case 'ENCERRADA':
      return { rotulo: 'Encerrada', classe: 'text-brand border-brand/40' };
    case 'CANCELADA':
      return { rotulo: 'Cancelada', classe: 'text-destructive border-destructive/40' };
    default:
      return { rotulo: item.status, classe: '' };
  }
}

function dataCurta(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** A data que importa muda com o status: parado há X dias, aberta em, encerrada em. */
function rotuloDeQuando(item: RascunhoDoFunilResponse): string {
  if (item.status === 'RASCUNHO' || item.status === 'RASCUNHO_EXPIRADO') {
    return `Parado há ${rotuloDiasParaEncerrar(diasParado(item.progressoAtualizadoEm))}`;
  }
  if (item.encerradaEm) return `${item.status === 'CANCELADA' ? 'Cancelada' : 'Encerrada'} em ${dataCurta(item.encerradaEm)}`;
  return `Aberta em ${dataCurta(item.abertaEm)}`;
}

/**
 * "Funil de discussões" — visibilidade de onde o wizard web de abertura está
 * sendo abandonado (`ultimaEtapaWizard`/`DiscussaoRascunhoEtapaEvento`, ver
 * `AberturaDiscussaoWizard.tsx`). As opções do filtro por etapa e os nomes
 * exibidos vêm do PRÓPRIO `resumo` — nunca duplicados aqui — porque o
 * servidor já é a fonte única do nome de cada etapa.
 */
export function FunilDeDiscussoesTab() {
  const [pagina, setPagina] = useState(1);
  const [etapa, setEtapa] = useState<string>(TODAS);
  const [status, setStatus] = useState<string>(TODOS);
  const [autorId, setAutorId] = useState<string>(TODOS);
  const [exportando, setExportando] = useState(false);

  const { data: resumo, isPending: resumoCarregando } = useAuditoriaAdminControllerResumoDoFunil();
  // Só médicos com pelo menos um rascunho do wizard — nunca a lista de
  // usuários inteira (`admin/usuarios`), que misturaria gente que nunca
  // abriu discussão nenhuma no meio do filtro.
  const { data: autores } = useAuditoriaAdminControllerAutoresDoFunil();

  const { data, isPending, isError, refetch } = useAuditoriaAdminControllerListarFunil({
    pagina,
    porPagina: 20,
    // Rascunho: quem está parado há mais tempo primeiro — é o que pede
    // atenção. O resto (abertas, encerradas, tudo junto): a mais recente.
    ...(filtraRascunho(status)
      ? { ordenarPor: 'progressoAtualizadoEm' as const, ordem: 'asc' as const }
      : { ordenarPor: 'abertaEm' as const, ordem: 'desc' as const }),
    ...(etapa !== TODAS ? { etapa: Number(etapa) } : {}),
    ...(status !== TODOS ? { status } : {}),
    ...(autorId !== TODOS ? { autorId } : {}),
  });

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  const maiorQuantidade = Math.max(1, ...(resumo?.etapas.map((e) => e.quantidade) ?? [1]));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-brand uppercase tracking-wide mb-3">
          Rascunhos parados agora, por etapa
        </h3>

        {resumoCarregando ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {resumo?.etapas.map((e) => (
              <div
                key={e.etapa}
                className="rounded-lg border border-brand/20 bg-surface-raised p-4"
              >
                <p className="text-xs text-muted-foreground">
                  Etapa {e.etapa + 1}/{TOTAL_ETAPAS_WIZARD}
                </p>
                <p
                  className="text-sm font-medium text-foreground truncate mt-0.5"
                  title={e.etapaNome}
                >
                  {e.etapaNome}
                </p>
                <p className="text-2xl font-semibold text-foreground mt-1.5">{e.quantidade}</p>
                <div className="mt-2 h-1.5 rounded-full bg-brand/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand"
                    style={{ width: `${(e.quantidade / maiorQuantidade) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {resumo && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {[
              { rotulo: 'Abertas agora', valor: resumo.abertas, filtro: 'ABERTA,EM_ATENDIMENTO' },
              { rotulo: 'Encerradas', valor: resumo.encerradas, filtro: 'ENCERRADA' },
              { rotulo: 'Canceladas', valor: resumo.canceladas, filtro: 'CANCELADA' },
            ].map((cartao) => (
              <button
                key={cartao.filtro}
                type="button"
                onClick={() => aoFiltrar(() => setStatus(cartao.filtro))}
                className="rounded-lg border border-brand/20 bg-surface-raised p-4 text-left hover:border-brand/50 transition-colors"
                aria-label={`${cartao.rotulo}: ${cartao.valor} — filtrar a lista`}
              >
                <p className="text-xs text-muted-foreground">{cartao.rotulo}</p>
                <p className="text-2xl font-semibold text-foreground mt-1">{cartao.valor}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Select value={etapa} onValueChange={(v) => aoFiltrar(() => setEtapa(v))}>
            <SelectTrigger className="sm:w-56" aria-label="Filtrar por etapa">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODAS}>Todas as etapas</SelectItem>
              {resumo?.etapas.map((e) => (
                <SelectItem key={e.etapa} value={String(e.etapa)}>
                  {rotuloEtapa(e.etapa, e.etapaNome)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
            <SelectTrigger className="sm:w-48" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              {FILTROS_DE_STATUS.map((f) => (
                <SelectItem key={f.valor} value={f.valor}>
                  {f.rotulo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {autores && autores.itens.length > 0 && (
            <Select value={autorId} onValueChange={(v) => aoFiltrar(() => setAutorId(v))}>
              <SelectTrigger className="sm:w-56" aria-label="Filtrar por médico">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os médicos</SelectItem>
                {autores.itens.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={() => setExportando(true)}>
          <Download className="w-4 h-4" />
          Exportar
        </Button>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Inbox}
            titulo="Nenhuma discussão"
            descricao="Nenhuma discussão bate com os filtros escolhidos."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Médico</TableHead>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead>Advisor</TableHead>
                    <TableHead>Quando</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((item) => {
                    const { rotulo, classe } = statusDaDiscussao(item);
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="text-foreground whitespace-nowrap">
                          {item.autorNome}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {item.tenantNome}
                        </TableCell>
                        <TableCell
                          className="text-foreground max-w-xs truncate"
                          title={item.assunto}
                        >
                          {item.assunto}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {item.origem === 'WEB' ? 'Web' : 'App'}
                        </TableCell>
                        <TableCell className="text-foreground whitespace-nowrap">
                          {/* Aberta pelo app não passou pelo wizard: não tem etapa. */}
                          {item.ultimaEtapaWizard === null
                            ? '—'
                            : rotuloEtapa(item.ultimaEtapaWizard, item.etapaNome ?? '')}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {item.advisorNome ?? '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {rotuloDeQuando(item)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={classe}>
                            {rotulo}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ExportarCsvDialogo
        aberto={exportando}
        aoFechar={() => setExportando(false)}
        titulo="Exportar funil de discussões"
        caminho="/v1/admin/auditoria/funil-discussoes/exportar.csv"
        descricaoCompleto="Exporta TODAS as discussões do funil (rascunhos do wizard, abertas, encerradas e canceladas), ignorando etapa, status, médico e qualquer período."
        descricaoPeriodo="Aplica o recorte da tela (etapa, status e médico) e limita à última atividade (progresso do wizard, ou a abertura) no intervalo escolhido (os dois lados são opcionais)."
        filtros={{
          etapa: etapa !== TODAS ? etapa : undefined,
          status: status !== TODOS ? status : undefined,
          autorId: autorId !== TODOS ? autorId : undefined,
        }}
      />
    </div>
  );
}
